import axios, { isAxiosError } from 'axios'
import { z } from "zod"
import { create } from 'zustand'
import { getViewProps, ViewerComponent } from '../components/Viewer'
import { createPromiseSingleFlight } from '../helpers/create-promise-single-flight'
import { GLOBAL_NAMESPACE, publish } from '../redux/pubsub'
import { getAppCtx, getLoginUrl } from '../hooks/use-app-ctx'

type AuthConfig = {
  repoURL: string
  localRepoURL?: string
  bbsparqlendpoint: string
  anonymousLogin?: boolean
  deleted?: boolean
}

type AuthContext = ReturnType<typeof deriveAuthContext>

export type AppLoginStatus
  = "UNINITIALIZED"
  | "NO_AUTHORIZATION_NEEDED"
  | "AUTHORIZED"
  | "FETCHING"
  | "FETCH_ERROR"
  | "NOT_LOGGED_IN"
  | "USER_NOT_PERMITTED"
  | "REPO_NOT_PERMITTED"
  | "BAD_CREDENTIALS"
  | "PENDING_AUTHORIZATION"
  | "WRONG_REPO"
  ;

export type UserResponse = {
  repo: string,
  userUri: string,
  userName: string,
  fullAccess: boolean,
  parameters?: {
    parameter?: string | null,
    parameter1?: string | null,
    parameter2?: string | null,
    parameter3?: string | null
  }
}

export type IsAuthorizedResponse = {
  status: "AUTHORIZED"
  success: true
  user: UserResponse
} | {
  status: Exclude<AppLoginStatus, "AUTHORIZED">
  success: false
}

export type AppLoginState = {
  authConfig: AuthConfig | null
  authContext: AuthContext | null

  status: AppLoginStatus
  success: boolean
  user: any | null /*what shape does user haz */

  /**
   * Connect to backend, monitor and keep alive login state
   * @param authConfig part of the app configuration related to Auth
   * @param viewerInstance instance of the Viewer react component [DEBT]
   */
  connectToBackend: (authConfig: AuthConfig, viewerInstance: ViewerComponent) => Promise<void>

  /**
   * Do a request to check if we are authorized
   * @param {boolean} [options.silent] - If true, suppresses logging output and alerts. Unexpected errors are still logged.
   */
  isAuthorized(
    options?: { silent: boolean },
  ): Promise<IsAuthorizedResponse>

  /**
   * Uses anonymous login or legacy no-login to automatically (re)auth
   */
  reauth(): Promise<boolean>

  goToLoginPage(): void
}

type Internal = {
  keepAliveInterval: ReturnType<typeof setTimeout> | null
}

const internal: Internal = {
  keepAliveInterval: null
}

const KEEP_ALIVE_INTERVAL = 5*60*1000 // 5 min

const reauthSingleFlight = createPromiseSingleFlight<string, boolean>()

export const useAppLoginStore = create<AppLoginState>()((set, get) => ({
  authConfig: null,
  authContext: null,
  status: "UNINITIALIZED",
  success: false,
  user: null,

  connectToBackend: async (authConfig, viewerInstance) => {
    const authContext = deriveAuthContext(authConfig, viewerInstance)
    set({ authConfig, authContext })

    if (internal.keepAliveInterval) {
      clearInterval(internal.keepAliveInterval)
    }

    if (!authContext.shouldConnectToBackend) {
      set({ status: "NO_AUTHORIZATION_NEEDED" })
      return
    }

    if (authContext.shouldAutoLogin) {
      const success = await get().reauth()
      if (!success) return get().goToLoginPage()
    }

    const resp = await get().isAuthorized()
    if (!resp.success) return get().goToLoginPage()

    internal.keepAliveInterval = setInterval(() => get().isAuthorized(), KEEP_ALIVE_INTERVAL)
  },

  isAuthorized: async (options) => {
    const authContext = requireAuthContext(get)
    const response = await isAuthorized(authContext, options)
    set({
      status: response.status,
      success: response.success,
      user: 'user' in response ? response.user : null
    })
    if (response.success) {
      publishUserParameters(response.user)
    }
    return response
  },

  reauth: () => {
    const authContext = requireAuthContext(get)
    const key = JSON.stringify(authContext)

    return reauthSingleFlight(key, async () => {
      const success = await reauth(authContext)
      set((prevState) => {
        if (success) {
          return {
            status: "AUTHORIZED",
            success: true
          }
        } else {
          return {
            // downgrade only AUTHORIZED to NOT_LOGGED_IN, keep any other status as-is
            status: prevState.status === "AUTHORIZED" ? "NOT_LOGGED_IN" : prevState.status,
            success: false,
            user: null
          }
        }
      })
      return success
    })
  },

  goToLoginPage: () => {
    const authContext = requireAuthContext(get)
    authContext.goToLoginPage()
  }
}))

function requireAuthContext(get: () => AppLoginState): AuthContext {
  const authContext = get().authContext
  if (!authContext) throw new Error('app-login-store is not yet connected!')
  return authContext
}

export function getRepoName(bbsparqlendpoint: string) {
  const params = new URLSearchParams(bbsparqlendpoint)
  return params.get('repo')
}

function deriveAuthContext(authConfig: AuthConfig, viewerInstance: ViewerComponent) {
  const { history, location } = viewerInstance.props // DEBT
  const { projectPath, viewerPath } = getViewProps(viewerInstance.props)

  const appCtx = getAppCtx(authConfig)
  if (appCtx.type === 'sparql-only') return {
    authConfig,
    shouldConnectToBackend: false,
    projectPath,
    viewerPath,
    repo: null,
    goToLoginPage() { throw new Error('not implemented') },
  }

  const shouldConnectToBackend = shouldConnectToWistorBackend(authConfig)

  const params = new URLSearchParams(authConfig.bbsparqlendpoint)
  const repoName = getRepoName(authConfig.bbsparqlendpoint)
  const repo2 = params.get('repo2') ?? ''
  const psw = params.get('psw')
  const login = params.get('login')
  const isLegacyAutoLogin = psw != null && login != null
  const isAnonymousLogin = Boolean(authConfig.anonymousLogin)
  const isAutoLogin = isLegacyAutoLogin || isAnonymousLogin
  const shouldAutoLogin = isAutoLogin && shouldConnectToWistorBackend(authConfig)

  const repoLoginUrl = constructRepoLoginPath(authConfig.bbsparqlendpoint, appCtx.urlSparql, viewerPath)
  const goToLoginPage = () => {
    history.replace(repoLoginUrl, window.location.pathname + window.location.search)
  }

  return {
    authConfig,
    shouldConnectToBackend,
    projectPath,
    viewerPath,
    repo: repoName,
    repo2,
    sparqlUrl: appCtx.urlSparql,
    isLoggedInUrl: appCtx.urlIsLoggedIn,
    isLegacyAutoLogin,
    psw,
    login,
    autoLoginUrl: appCtx.urlLogin,
    isAnonymousLogin,
    anonymousLoginUrl: appCtx.urlAnonymousLogin,
    shouldAutoLogin,
    repoLoginUrl,
    goToLoginPage
  }
}

export type AppLoginCredentials = {
  username: string
  password: string
}

/**
 * Invoke legacy login with credentials.
 * This is *not* part of the store, as it is used at a point where the viewer config is not always available.
 *
 * @param credentials username and password
 * @param repoName repository name
 * @param repoURL the sparql url assumed to be for the wistor-backend
 */
export async function appLogin(
  credentials: AppLoginCredentials,
  repoName: string,
  repoURL: string
): Promise<{
  success: boolean,
  status: AppLoginStatus
}> {
  const LoginResponseSchema = z.object({
    loggedIn: z.string(), // boolean-string with casing variations...
    repo: z.string().optional(),
    parameters: z.record(z.string(), z.any()).optional(),
    message: z.string().optional()
  })

  const url = getLoginUrl(repoName, repoURL)

  try {
    const response = await axios.post(url, {
      login: credentials.username,
      psw: credentials.password
    }, {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json"
      }
    })

    const repoLoginResponse = LoginResponseSchema.parse(response.data)
    if (repoLoginResponse.loggedIn.toLowerCase() !== "true") {
      return { success: false, status: "BAD_CREDENTIALS" }
    }

    if (repoLoginResponse.repo !== repoName) {
      return { success: false, status: "REPO_NOT_PERMITTED" }
    }

    // DEBT: inconsistent with isAuthorized() and may not be needed
    if (repoLoginResponse.parameters) {
      document.userParameters = repoLoginResponse.parameters
    }

    return {
      status: "AUTHORIZED",
      success: true
    }
  } catch (error) {
    if (!isAxiosError(error)) {
      throw error
    }

    console.error(error)
    return { success: false, status: "FETCH_ERROR" }
  }
}

const oidcLoginStatusEnum = z.enum([
  "NOT_AUTHENTICATED",
  "REPO_NOT_PERMITTED",
  "USER_NOT_PERMITTED",
  "PENDING_AUTHORIZATION",
  "AUTHORIZED"
])

const IsLoggedInResponseSchema = z.union([
  z.object({
    loggedIn: z.literal("FALSE"),
    repo: z.string(),
    oidcLoginStatus: oidcLoginStatusEnum.optional()
  }),
  z.object({
    loggedIn: z.literal("TRUE"),
    repo: z.string(),
    oidcLoginStatus: oidcLoginStatusEnum.optional(),
    fullAccess: z.boolean(),
    version: z.string(),
    userName: z.string(),
    userUri: z.string(),
    parameters: z.object({
      parameter: z.string().optional().nullable(),
      parameter1: z.string().optional().nullable(),
      parameter2: z.string().optional().nullable(),
      parameter3: z.string().optional().nullable(),
    }).optional()
  })
])

async function isAuthorized(authContext: AuthContext, options = { silent: false }): Promise<IsAuthorizedResponse> {

  const { isLoggedInUrl, viewerPath, repo, repo2 } = authContext
  if (!isLoggedInUrl) return {
    status: 'NO_AUTHORIZATION_NEEDED',
    success: false,
  }

  let response
  try {
    const axiosResponse = await axios.get(isLoggedInUrl, {
      withCredentials: true,
      params: { viewerPath },
    })
    response = IsLoggedInResponseSchema.parse(axiosResponse.data)

  } catch(e) {
    console.error("Could not fetch user data", e)

    /* DEBT:
     *  It should be passed more explicitly that it's really a single login error (not just with 403)
     *  @see IsLoggedInServlet#doPost()
     */
    if (isAxiosError(e) && e.response?.status === 403) {
      return { success: false, status: "USER_NOT_PERMITTED" }
    }
  }

  const loginStatus = (() => {
    if (!response) {
      if (!options?.silent) {
        console.error('no user data was fetched')
      }
      return "FETCH_ERROR"
    }

    if (response.loggedIn == null) {
      console.error('no user data was fetched (22)')
      return "FETCH_ERROR"
    }

    if (response.oidcLoginStatus === 'USER_NOT_PERMITTED' || response.oidcLoginStatus === 'REPO_NOT_PERMITTED') {
      if (!options?.silent) {
        alert('Your account does not have sufficient permissions for this application.')
      }
      return response.oidcLoginStatus
    }

    if (response.oidcLoginStatus === "PENDING_AUTHORIZATION") {
      return "PENDING_AUTHORIZATION"
    }

    if (response.loggedIn !== 'TRUE') return "NOT_LOGGED_IN"

    if (repo == null || response.repo == null) return "FETCH_ERROR"

    if (repo.toLowerCase() !== response.repo.toLowerCase() && repo2.toLowerCase() !== response.repo.toLowerCase()) {
      if (!options?.silent) {
        alert('wrong repo. This account is not valid for this application.')
      }
      return "WRONG_REPO"
    }

    return "AUTHORIZED"
  })()

  if (loginStatus === "AUTHORIZED") {
    if (!response || response.loggedIn !== 'TRUE') throw new Error('unexpected response state')
    return {
      user: {
        userName: response.userName,
        userUri: response.userUri,
        repo: response.repo,
        fullAccess: response.fullAccess,
        parameters: response.parameters
      },
      success: true,
      status: loginStatus
    }
  } else {
    return { success: false, status: loginStatus }
  }
}

async function reauth(authContext: AuthContext): Promise<boolean> {
  const {
    isLegacyAutoLogin, psw, login, autoLoginUrl,
    isAnonymousLogin, anonymousLoginUrl
  } = authContext
  if (!authContext.shouldAutoLogin) return false

  let success
  if (isLegacyAutoLogin) {
    // DEPRECATED Legacy auto-login with plaintext psw + login
    try {
      const response = await axios({
        method: 'post',
        url: autoLoginUrl,
        withCredentials: true,
        data: { login, psw },
      })
      success = response.data?.loggedIn?.toLowerCase?.() === 'true'

    } catch(error) {
      if (!isAxiosError(error)) throw error
      success = !(error?.response?.status === 401 || error?.response?.status === 403)
      if (!success) alert('Default login credentials are incorrect. Login with valid credentials and change it to fix this.')
    }
  } else if (isAnonymousLogin) {
    if (!anonymousLoginUrl) {
      alert('Could not construct anonymous login URL. Please check if repoURL configured correctly')
      return false
    }

    try {
      const response = await axios({
        method: 'post',
        url: anonymousLoginUrl,
        withCredentials: true
      })
      success = response.data?.user != null

    } catch(error) {
      success = false
      if (!isAxiosError(error)) throw error
      if (error?.response?.data?.status === 'NOT_AUTHORIZED') {
        alert('This app is not properly configured for anonymous access.')
      }
    }
  } else {
    throw new Error('should have either legacy auto login or anonymous login at this point')
  }

  return success
}

declare global {
  interface Document {
    userParameters?: {
      userName?: string,
      userUri?: string,
      parameter?: string | null,
      parameter1?: string | null,
      parameter2?: string | null,
      parameter3?: string | null,
    }
  }
}

function publishUserParameters(user: UserResponse) {
  publish('user.fullAccess', Boolean(user.fullAccess), GLOBAL_NAMESPACE, 'publish user parameters')

  // DEBT
  if (user.parameters != null) document.userParameters = user.parameters
  if (document.userParameters == null) document.userParameters = {}
  document.userParameters.userUri = user.userUri
  document.userParameters.userName = user.userName
}

function constructRepoLoginPath(bbsparqlendpoint: string, repoUrl: string, viewerPath: string): string {
  const repoLoginParams = new URLSearchParams(bbsparqlendpoint)
  repoLoginParams.set('repoUrl', repoUrl)
  repoLoginParams.set('viewerPath', viewerPath)

  return '/loginbb?id=' + encodeURIComponent(btoa('?' + repoLoginParams.toString()))
}

function shouldConnectToWistorBackend(config: AuthConfig) {
  const isConnectedToLocalRepo = config.localRepoURL?.includes(':7200') && window.location.host === 'localhost:3000'
  if (isConnectedToLocalRepo) return false
  if (config.deleted) return false
  if (window.location.search.includes("debug=skiplogin")) return false
  return Boolean(config.bbsparqlendpoint?.includes?.('repo='))
}
